import {
  areaName,
  assetTypeName,
  INCIDENT_STATUS_LABELS,
  MONITOR_STATUS_LABELS,
  MONITOR_STATUSES,
  ROUTE_CATEGORIES,
  ROUTE_CATEGORY_LABELS,
  ROUTE_STATUS_LABELS,
  ROUTE_STATUSES,
  type DashboardData,
} from '@ftthnms/shared';
import { Badge, RegionChip } from '@ftthnms/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  Bell,
  Boxes,
  Cable,
  GitFork,
  House,
  Inbox,
  Map as MapIcon,
  Server,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { AreaChart, DonutChart, Sparkline, type AreaPoint, type DonutSegment } from '../components/charts';
import { api, metres, nf, nf1, qs } from '../lib/api';
import { ago, toIndicator } from '../lib/monitoring';
import { useSession } from '../lib/session';
import { AssetGlyph } from '../map/AssetGlyph';

const TYPE_ORDER = ['OLT', 'SERVER', 'AP', 'ODC', 'ODP', 'JC', 'POLE', 'CPE'];
const TYPE_COLOR: Record<string, string> = {
  OLT: 'var(--asset-olt)',
  SERVER: 'var(--asset-server)',
  AP: 'var(--asset-ap)',
  ODC: 'var(--asset-odc)',
  ODP: 'var(--asset-odp)',
  JC: 'var(--asset-jc)',
  POLE: 'var(--asset-pole)',
  CPE: 'var(--asset-cpe)',
};
const CATEGORY_COLOR: Record<string, string> = {
  backbone: 'var(--chart-1)',
  feeder: 'var(--chart-2)',
  distribution: 'var(--chart-3)',
  drop: 'var(--chart-4)',
};
const MON_TONE: Record<string, string> = {
  online: 'var(--success)',
  warning: 'var(--warning)',
  offline: 'var(--danger)',
  maintenance: 'var(--purple, #a78bfa)',
  unknown: 'var(--text-muted)',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const MONTHS_LONG = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];
const pct = (used: number, total: number) => (total > 0 ? Math.round((used / total) * 1000) / 10 : 0);
/** Compact axis labels: 0, 500 m, 1,5 km / 0, 250, 1,2 rb. */
const kmAxis = (v: number) =>
  v === 0 ? '0' : v >= 1000 ? `${nf1.format(v / 1000)} km` : `${nf.format(v)} m`;
const countAxis = (v: number) => (v >= 1000 ? `${nf1.format(v / 1000)} rb` : nf.format(v));

/** Dashboard (PRD §5.8, DSH-01..06), scoped to the topbar area picker; teknisi see only their area. */
export function DashboardPage() {
  const { area, areaOptions, ability } = useSession();
  const q = useQuery({
    queryKey: ['dashboard', area],
    queryFn: () => api<DashboardData>(`/api/v1/stats/dashboard${qs({ area })}`),
    refetchInterval: 120_000,
  });
  const d = q.data;
  const scopeLabel = area ? areaName(area) : areaOptions === 'all' ? 'semua wilayah' : 'wilayah Anda';
  const now = new Intl.DateTimeFormat('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Asia/Jakarta',
  }).format(d ? new Date(d.generatedAt) : new Date());
  const canMon = ability.can('read', 'Monitoring');
  const canAlert = ability.can('read', 'Alert');

  return (
    <div className="fn-page-body">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 style={{ margin: 0, font: '800 22px/30px var(--font-sans)', letterSpacing: '-.02em' }}>
            Ringkasan Jaringan
          </h2>
          <p className="fn-card__desc" style={{ marginTop: 2 }}>
            Data per {now} WIB · {scopeLabel}
          </p>
        </div>
        <Link to="/map" className="fn-btn fn-btn--primary fn-btn--sm">
          <MapIcon className="i i--sm" aria-hidden /> Buka Peta
        </Link>
      </div>

      {d && d.assets.sample > 0 && (
        <div className="fn-alert fn-alert--info">
          <div className="fn-alert__body">
            <span className="fn-alert__title">Termasuk {nf.format(d.assets.sample)} aset contoh</span>
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              Aset contoh (bertanda "(contoh)") dihapus saat go-live; data asli diinput langsung atau diimpor
              dari KMZ.
            </span>
          </div>
        </div>
      )}

      <Kpis d={d} loading={q.isPending} canMon={canMon} canAlert={canAlert} />

      <div className="fn-dash-row fn-dash-row--chart">
        <GrowthCard d={d} />
        <CompositionCard d={d} canMon={canMon} />
      </div>

      <div className="fn-dash-row fn-dash-row--three">
        <Occupancy d={d} />
        {canMon ? <MonitoringCard d={d} canAlert={canAlert} /> : <AssetsCard d={d} />}
        <PonCard d={d} />
      </div>

      <div className="fn-dash-row fn-dash-row--detail">
        <RoutesCard d={d} />
        {canMon && <AssetsCard d={d} />}
      </div>

      {d && d.areas.length > 1 && <AreaTable d={d} canMon={canMon} canAlert={canAlert} />}
    </div>
  );
}

// ---------------------------------------------------------------- shared bits

function Card({
  title,
  desc,
  action,
  children,
  bodyClass,
}: {
  title: string;
  desc?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  bodyClass?: string;
}) {
  return (
    <section className="fn-card fn-dash-card">
      <div className="fn-card__header">
        <div style={{ minWidth: 0 }}>
          <h3 className="fn-card__title">{title}</h3>
          {desc && <p className="fn-card__desc">{desc}</p>}
        </div>
        {action}
      </div>
      <div className={`fn-card__body fn-dash-card__body${bodyClass ? ` ${bodyClass}` : ''}`}>{children}</div>
    </section>
  );
}

function Empty({ icon: Icon = Inbox, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <div className="fn-dash-empty">
      <span className="fn-tile">
        <Icon className="i" aria-hidden />
      </span>
      <span>{children}</span>
    </div>
  );
}

function Delta({ value, format }: { value: number; format: (v: number) => string }) {
  return value > 0 ? (
    <span className="fn-delta fn-delta--up">▲ +{format(value)} bulan ini</span>
  ) : (
    <span className="fn-delta fn-delta--flat">Tidak ada penambahan bulan ini</span>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="fn-segmented fn-dash-seg" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          className="fn-segmented__item"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- KPIs

function Kpis({
  d,
  loading,
  canMon,
  canAlert,
}: {
  d?: DashboardData;
  loading: boolean;
  canMon: boolean;
  canAlert: boolean;
}) {
  const trend = d?.trend ?? [];
  const last = trend[trend.length - 1];
  const odpPct = pct(d?.occupancy.odp.used ?? 0, d?.occupancy.odp.ports ?? 0);
  const online = d?.monitoring.counts.online ?? 0;
  const monTotal = d?.monitoring.total ?? 0;
  const v = (s: string) => (loading ? '–' : s);

  return (
    <div className="fn-dash-kpis">
      <Kpi
        to="/customers"
        label="Pelanggan"
        icon={House}
        tone="success"
        value={v(nf.format(d?.customers.total ?? 0))}
        unit="CPE"
        meta={`${nf.format(d?.customers.connected ?? 0)} terhubung`}
        delta={<Delta value={last?.customers ?? 0} format={(x) => nf.format(x)} />}
        visual={
          <Sparkline
            values={trend.map((t) => t.customersTotal)}
            color="var(--success)"
            label="Tren total pelanggan 12 bulan"
          />
        }
      />
      <Kpi
        to="/assets"
        label="Titik distribusi"
        icon={GitFork}
        tone="cyan"
        value={v(nf.format((d?.assets.byType.ODC ?? 0) + (d?.assets.byType.ODP ?? 0)))}
        unit="ODC + ODP"
        meta={`${nf.format(d?.occupancy.odp.used ?? 0)}/${nf.format(d?.occupancy.odp.ports ?? 0)} port`}
        delta={<span className="fn-delta fn-delta--flat">Okupansi ODP {nf1.format(odpPct)}%</span>}
        visual={
          <div className="fn-dash-kpi__meter" aria-label={`Okupansi ODP ${nf1.format(odpPct)}%`}>
            <span style={{ width: `${Math.min(100, odpPct)}%` }} />
          </div>
        }
      />
      <Kpi
        to="/routes"
        label="Panjang kabel"
        icon={Cable}
        tone="primary"
        value={v(d ? metres(d.routes.lengthM) : '–')}
        meta={`${nf.format(d?.routes.total ?? 0)} jalur`}
        delta={<Delta value={last?.cableM ?? 0} format={metres} />}
        visual={
          <Sparkline
            values={trend.map((t) => t.cableTotalM)}
            color="var(--chart-1)"
            label="Tren total kabel terpasang 12 bulan"
          />
        }
      />
      {canMon ? (
        <Kpi
          to="/monitoring"
          label="Perangkat online"
          icon={Activity}
          tone={(d?.monitoring.counts.offline ?? 0) > 0 ? 'danger' : 'success'}
          value={v(`${nf.format(online)}/${nf.format(monTotal)}`)}
          unit="dipantau"
          meta={
            canAlert
              ? `${nf.format(d?.incidents.open ?? 0)} insiden aktif`
              : `${nf.format(d?.monitoring.counts.offline ?? 0)} offline`
          }
          delta={
            <span className="fn-delta fn-delta--flat">
              {monTotal > 0 ? `${nf1.format(pct(online, monTotal))}% online` : 'Belum ada perangkat dipantau'}
            </span>
          }
          visual={
            <div className="fn-bar fn-dash-kpi__stack" aria-hidden>
              {MONITOR_STATUSES.map((s) => (
                <i
                  key={s}
                  style={{
                    width: `${monTotal > 0 ? ((d?.monitoring.counts[s] ?? 0) / monTotal) * 100 : 0}%`,
                    background: MON_TONE[s],
                  }}
                />
              ))}
            </div>
          }
        />
      ) : (
        <Kpi
          to="/assets"
          label="Total aset"
          icon={Boxes}
          tone="purple"
          value={v(nf.format(d?.assets.total ?? 0))}
          unit="aset"
          meta={`${nf.format(d?.assets.byType.POLE ?? 0)} tiang`}
          delta={<span className="fn-delta fn-delta--flat">Semua jenis aset</span>}
          visual={null}
        />
      )}
    </div>
  );
}

function Kpi({
  to,
  label,
  icon: Icon,
  tone,
  value,
  unit,
  meta,
  delta,
  visual,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  tone: string;
  value: string;
  unit?: string;
  meta: string;
  delta: ReactNode;
  visual: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="fn-card fn-kpi fn-card--interactive fn-dash-kpi"
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div className="fn-kpi__head">
        <span className="fn-kpi__label">{label}</span>
        <span className={`fn-tile fn-tile--${tone}`}>
          <Icon className="i" aria-hidden />
        </span>
      </div>
      <div className="fn-kpi__value">
        <span className="fn-kpi__number">{value}</span>
        {unit && <span className="fn-kpi__unit">{unit}</span>}
      </div>
      <div className="fn-dash-kpi__foot">
        {delta}
        <span className="fn-kpi__meta">{meta}</span>
      </div>
      <div className="fn-dash-kpi__visual">{visual}</div>
    </Link>
  );
}

// ---------------------------------------------------------------- growth + composition

type Series = 'cable' | 'customers';

function GrowthCard({ d }: { d?: DashboardData }) {
  const [series, setSeries] = useState<Series>('cable');
  const [months, setMonths] = useState<'6' | '12'>('12');
  const trend = (d?.trend ?? []).slice(months === '6' ? -6 : -12);
  const cable = series === 'cable';
  const points: AreaPoint[] = trend.map((t) => {
    const m = Number(t.month.slice(5, 7)) - 1;
    return {
      label: MONTHS[m]!,
      title: `${MONTHS_LONG[m]} ${t.month.slice(0, 4)}`,
      value: cable ? t.cableTotalM : t.customersTotal,
      added: cable ? t.cableM : t.customers,
    };
  });
  const last = points[points.length - 1];
  const format = cable ? metres : (v: number) => `${nf.format(v)} pelanggan`;

  return (
    <section className="fn-card fn-dash-card">
      <div className="fn-card__header fn-dash-chart-head">
        <div style={{ minWidth: 0 }}>
          <h3 className="fn-card__title">Pertumbuhan jaringan</h3>
          <p className="fn-card__desc">
            {cable
              ? 'Total kabel terpasang (terpasang + maintenance) di akhir tiap bulan'
              : 'Total pelanggan (CPE) di akhir tiap bulan'}
          </p>
        </div>
        <div className="fn-dash-controls">
          <Segmented
            label="Seri grafik"
            value={series}
            onChange={setSeries}
            options={[
              ['cable', 'Kabel'],
              ['customers', 'Pelanggan'],
            ]}
          />
          <Segmented
            label="Periode"
            value={months}
            onChange={setMonths}
            options={[
              ['6', '6 bln'],
              ['12', '12 bln'],
            ]}
          />
        </div>
      </div>
      <div className="fn-card__body fn-dash-card__body" style={{ gap: 12 }}>
        <div className="fn-dash-headline">
          <span className="fn-dash-headline__value">
            {last ? (cable ? metres(last.value) : nf.format(last.value)) : '–'}
          </span>
          {!cable && <span className="fn-kpi__unit">pelanggan</span>}
          {last && <Delta value={last.added} format={cable ? metres : (v) => nf.format(v)} />}
        </div>
        <AreaChart
          points={points}
          color={cable ? 'var(--chart-1)' : 'var(--success)'}
          format={format}
          axisFormat={cable ? kmAxis : countAxis}
          label={cable ? 'Total kabel terpasang per bulan' : 'Total pelanggan per bulan'}
        />
      </div>
    </section>
  );
}

type Composition = 'cable' | 'assets' | 'devices';

function CompositionCard({ d, canMon }: { d?: DashboardData; canMon: boolean }) {
  const [tab, setTab] = useState<Composition>('cable');
  let segments: DonutSegment[] = [];
  let center = '';
  let centerLabel = '';
  let desc = '';

  if (tab === 'cable') {
    segments = ROUTE_CATEGORIES.map((c) => {
      const v = d?.routes.byCategory[c]?.lengthM ?? 0;
      return {
        key: c,
        label: ROUTE_CATEGORY_LABELS[c],
        value: v,
        color: CATEGORY_COLOR[c]!,
        display: metres(v),
      };
    });
    center = metres(d?.routes.lengthM ?? 0);
    centerLabel = `${nf.format(d?.routes.total ?? 0)} jalur`;
    desc = 'Panjang kabel per kategori jalur';
  } else if (tab === 'assets') {
    segments = TYPE_ORDER.map((t) => ({
      key: t,
      label: assetTypeName(t),
      value: d?.assets.byType[t] ?? 0,
      color: TYPE_COLOR[t]!,
      display: nf.format(d?.assets.byType[t] ?? 0),
    })).filter((s) => s.value > 0 || ['ODC', 'ODP', 'CPE'].includes(s.key));
    center = nf.format(d?.assets.total ?? 0);
    centerLabel = 'aset';
    desc = 'Komposisi aset per jenis';
  } else {
    segments = MONITOR_STATUSES.map((s) => ({
      key: s,
      label: MONITOR_STATUS_LABELS[s],
      value: d?.monitoring.counts[s] ?? 0,
      color: MON_TONE[s]!,
      display: nf.format(d?.monitoring.counts[s] ?? 0),
    }));
    center = nf.format(d?.monitoring.total ?? 0);
    centerLabel = 'perangkat dipantau';
    desc = 'Status perangkat yang dipantau';
  }

  const tabs: Array<[Composition, string]> = [
    ['cable', 'Kabel'],
    ['assets', 'Aset'],
    ...(canMon ? [['devices', 'Perangkat'] as [Composition, string]] : []),
  ];

  return (
    <section className="fn-card fn-dash-card">
      <div className="fn-card__header">
        <div style={{ minWidth: 0 }}>
          <h3 className="fn-card__title">Komposisi</h3>
          <p className="fn-card__desc">{desc}</p>
        </div>
        <Segmented label="Jenis komposisi" value={tab} onChange={setTab} options={tabs} />
      </div>
      <div className="fn-card__body fn-dash-card__body" style={{ justifyContent: 'center' }}>
        <DonutChart segments={segments} center={center} centerLabel={centerLabel} label={desc} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- detail cards

function Capacity({ label, used, total }: { label: string; used: number; total: number }) {
  const p = pct(used, total);
  return (
    <div className={`fn-capacity${p >= 100 ? ' fn-capacity--full' : p >= 80 ? ' fn-capacity--high' : ''}`}>
      <div className="fn-capacity__legend">
        <span>{label}</span>
        <span>
          <strong>{nf.format(used)}</strong> / {nf.format(total)} port · {nf1.format(p)}%
        </span>
      </div>
      <div className="fn-capacity__track">
        <span className="fn-capacity__used" style={{ width: `${Math.min(100, p)}%` }} />
      </div>
    </div>
  );
}

function Occupancy({ d }: { d?: DashboardData }) {
  const o = d?.occupancy;
  return (
    <Card title="Okupansi jaringan" desc="Port output yang sudah tersambung ke titik berikutnya">
      <Capacity label="ODP" used={o?.odp.used ?? 0} total={o?.odp.ports ?? 0} />
      <Capacity label="ODC" used={o?.odc.used ?? 0} total={o?.odc.ports ?? 0} />
      <div className="fn-dash-sub">
        <div className="fn-panel__label">
          ODP penuh / hampir penuh{' '}
          {o && o.hotOdpCount > 0 && <Badge tone="warning">{nf.format(o.hotOdpCount)}</Badge>}
        </div>
        {!o || o.hotOdps.length === 0 ? (
          <Empty>Tidak ada ODP dengan okupansi 80% ke atas.</Empty>
        ) : (
          <div className="flex flex-col gap-1">
            {o.hotOdps.slice(0, 5).map((h) => (
              <Link
                key={h.id}
                to="/map"
                search={{ focus: h.id }}
                className="fn-layer"
                style={{ textDecoration: 'none' }}
              >
                <span className="fn-layer__name">
                  <span className="mono">{h.code}</span>{' '}
                  <span className="fn-hint fn-truncate" style={{ display: 'inline' }}>
                    {h.name}
                  </span>
                </span>
                <Badge tone={h.used >= h.total ? 'danger' : 'warning'}>
                  {h.used}/{h.total}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function MonitoringCard({ d, canAlert }: { d?: DashboardData; canAlert: boolean }) {
  const m = d?.monitoring;
  const total = Math.max(1, m?.total ?? 0);
  return (
    <Card
      title="Monitoring & insiden"
      desc={
        canAlert && d
          ? `${nf.format(d.incidents.open)} aktif · ${nf.format(d.incidents.acknowledged)} ditangani · ${nf.format(d.incidents.resolvedToday)} selesai hari ini`
          : `${nf.format(m?.total ?? 0)} perangkat dipantau`
      }
      action={
        <Link to={canAlert ? '/alerts' : '/monitoring'} className="fn-link" style={{ fontSize: 12 }}>
          {canAlert ? 'Semua insiden' : 'Status perangkat'}
        </Link>
      }
    >
      <div className="fn-bar" style={{ height: 10 }} aria-label="Sebaran status perangkat">
        {MONITOR_STATUSES.map((s) => (
          <i
            key={s}
            title={`${MONITOR_STATUS_LABELS[s]}: ${m?.counts[s] ?? 0}`}
            style={{ width: `${((m?.counts[s] ?? 0) / total) * 100}%`, background: MON_TONE[s] }}
          />
        ))}
      </div>
      <div className="fn-dash-legend">
        {MONITOR_STATUSES.map((s) => (
          <span key={s}>
            <span className="fn-swatch" style={{ background: MON_TONE[s], width: 8, height: 8 }} />
            {MONITOR_STATUS_LABELS[s]} <strong>{nf.format(m?.counts[s] ?? 0)}</strong>
          </span>
        ))}
      </div>
      <div className="fn-dash-sub">
        <div className="fn-panel__label">Insiden terbaru</div>
        {!canAlert ? (
          <Empty icon={Bell}>Riwayat insiden tidak tersedia untuk peran Anda.</Empty>
        ) : !d || d.incidents.recent.length === 0 ? (
          <Empty icon={Bell}>Belum ada insiden.</Empty>
        ) : (
          <div className="fn-feed">
            {d.incidents.recent.slice(0, 5).map((i) => (
              <div key={i.id} className="fn-feed__item" style={{ paddingInline: 0 }}>
                <span
                  className={`fn-status fn-status--${i.status === 'resolved' ? 'online' : toIndicator(i.deviceStatus ?? 'offline')}`}
                  style={{ paddingTop: 5 }}
                >
                  <span className="fn-status__dot" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="fn-feed__title">
                    <Link to="/map" search={{ focus: i.assetId }} className="fn-link">
                      {i.assetCode}
                    </Link>{' '}
                    offline
                  </div>
                  <div className="fn-feed__meta">
                    {INCIDENT_STATUS_LABELS[i.status]}
                    {i.status === 'resolved'
                      ? i.resolvedBy
                        ? ` oleh ${i.resolvedBy}`
                        : ' · pulih otomatis'
                      : i.acknowledgedBy
                        ? ` · ${i.acknowledgedBy}`
                        : ''}
                  </div>
                </div>
                <div className="fn-feed__time">{ago(i.startedAt)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function PonCard({ d }: { d?: DashboardData }) {
  const pons = d?.occupancy.pons ?? [];
  return (
    <section className="fn-card fn-dash-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Kapasitas PON per OLT</h3>
          <p className="fn-card__desc">Port PON terpakai dan pelanggan di belakangnya</p>
        </div>
      </div>
      {pons.length === 0 ? (
        <div className="fn-card__body fn-dash-card__body">
          <Empty icon={Server}>Belum ada OLT di wilayah ini.</Empty>
        </div>
      ) : (
        <div className="fn-table-wrap" style={{ flex: 1 }}>
          <table className="fn-table">
            <thead>
              <tr>
                <th>OLT</th>
                <th style={{ width: '40%' }}>PON terpakai</th>
                <th className="is-num">Pelanggan</th>
              </tr>
            </thead>
            <tbody>
              {pons.map((p) => (
                <tr key={p.oltId}>
                  <td>
                    <Link to="/map" search={{ focus: p.oltId }} className="fn-link mono">
                      {p.oltCode}
                    </Link>
                  </td>
                  <td>
                    {p.ports > 0 && p.ports - p.used === 0 ? (
                      <Badge tone="danger">
                        penuh {p.used}/{p.ports}
                      </Badge>
                    ) : (
                      <Capacity label="" used={p.used} total={p.ports} />
                    )}
                  </td>
                  <td className="is-num">{nf.format(p.customers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RoutesCard({ d }: { d?: DashboardData }) {
  const r = d?.routes;
  const rows = ROUTE_CATEGORIES.filter((c) => (r?.byCategory[c]?.count ?? 0) > 0);
  return (
    <Card
      title="Jalur kabel per kategori"
      desc={`${nf.format(r?.total ?? 0)} jalur · ${metres(r?.lengthM ?? 0)}`}
      action={
        <Link to="/routes" className="fn-link" style={{ fontSize: 12 }}>
          Lihat jalur
        </Link>
      }
    >
      {rows.length === 0 ? (
        <Empty icon={Cable}>Belum ada jalur kabel.</Empty>
      ) : (
        <div className="fn-dash-cats">
          {rows.map((c) => {
            const e = r!.byCategory[c]!;
            return (
              <div key={c} className="fn-dash-cat">
                <div className="fn-dash-cat__head">
                  <span
                    className="fn-swatch"
                    style={{ background: CATEGORY_COLOR[c], width: 10, height: 10 }}
                  />
                  <span className="fn-dash-cat__name">{ROUTE_CATEGORY_LABELS[c]}</span>
                  <span className="fn-dash-cat__meta">{nf.format(e.count)} jalur</span>
                  <strong>{metres(e.lengthM)}</strong>
                </div>
                <div className="fn-bar" style={{ height: 8 }}>
                  {ROUTE_STATUSES.map((s) => (
                    <i
                      key={s}
                      title={`${ROUTE_STATUS_LABELS[s]}: ${metres(e.byStatus[s]?.lengthM ?? 0)}`}
                      style={{
                        width: `${e.lengthM > 0 ? ((e.byStatus[s]?.lengthM ?? 0) / e.lengthM) * 100 : 0}%`,
                        background: `var(--route-${s})`,
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="fn-dash-legend" style={{ marginTop: 'auto' }}>
        {ROUTE_STATUSES.map((s) => (
          <span key={s}>
            <span className="fn-swatch" style={{ background: `var(--route-${s})`, width: 8, height: 8 }} />
            {ROUTE_STATUS_LABELS[s]} <strong>{metres(r?.byStatus[s]?.lengthM ?? 0)}</strong>
          </span>
        ))}
        <span>
          Tiang otomatis <strong>{nf.format(r?.poles.auto ?? 0)}</strong>
        </span>
        <span>
          Tiang manual <strong>{nf.format(r?.poles.manual ?? 0)}</strong>
        </span>
      </div>
    </Card>
  );
}

function AssetsCard({ d }: { d?: DashboardData }) {
  return (
    <Card
      title="Aset per jenis"
      desc={`${nf.format(d?.assets.total ?? 0)} aset tercatat`}
      action={
        <Link to="/assets" className="fn-link" style={{ fontSize: 12 }}>
          Lihat daftar
        </Link>
      }
    >
      <div className="fn-dash-types">
        {TYPE_ORDER.map((code) => {
          const n = d?.assets.byType[code] ?? 0;
          return (
            <Link
              key={code}
              to="/assets"
              className={`fn-dash-type${n === 0 ? ' is-zero' : ''}`}
              style={{ textDecoration: 'none' }}
            >
              <AssetGlyph type={code} />
              <span className="fn-dash-type__name">{assetTypeName(code)}</span>
              <strong>{nf.format(n)}</strong>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function AreaTable({ d, canMon, canAlert }: { d: DashboardData; canMon: boolean; canAlert: boolean }) {
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Perbandingan wilayah</h3>
          <p className="fn-card__desc">
            Aset, pelanggan, okupansi ODP, panjang kabel{canMon ? ', uptime 24 jam' : ''}
            {canAlert ? ' dan insiden aktif' : ''}
          </p>
        </div>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Wilayah</th>
              <th className="is-num">Aset</th>
              <th className="is-num">Pelanggan</th>
              <th style={{ width: 220 }}>Okupansi ODP</th>
              <th className="is-num">Kabel</th>
              {canMon && <th className="is-num">Uptime 24 jam</th>}
              {canAlert && <th className="is-num">Insiden aktif</th>}
            </tr>
          </thead>
          <tbody>
            {d.areas.map((a) => (
              <tr key={a.code}>
                <td>
                  <RegionChip code={a.code} />
                </td>
                <td className="is-num">{nf.format(a.assets)}</td>
                <td className="is-num">{nf.format(a.customers)}</td>
                <td>
                  <Capacity label="" used={a.odpUsed} total={a.odpPorts} />
                </td>
                <td className="is-num">{metres(a.cableM)}</td>
                {canMon && (
                  <td className="is-num">{a.uptimePct === null ? '—' : `${nf1.format(a.uptimePct)}%`}</td>
                )}
                {canAlert && (
                  <td className="is-num">
                    {a.openIncidents > 0 ? <Badge tone="danger">{a.openIncidents}</Badge> : '0'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
